import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  ArrowUpDown,
  ChartPie,
  Copy,
  Pencil,
  Plus,
  Trash2,
  Zap,
} from 'lucide-react'
import ActiveMark from '../../components/ActiveMark'
import Button from '../../components/Button'
import { useRemembered } from '../../components/listMemory'
import ConfirmDialog from '../../components/ConfirmDialog'
import DataTable from '../../components/DataTable'
import FilterBar from '../../components/FilterBar'
import OutcomeShare from '../../components/OutcomeShare'
import PageHeader from '../../components/PageHeader'
import RowActions from '../../components/RowActions'
import StatusBadge from '../../components/StatusBadge'
import { useToast } from '../../components/toastContext'
import ProgramOrderModal from './ProgramOrderModal'
import { cloneProgramAsCopy, deleteProgram, listPrograms, saveProgramsOrder } from './programsService'
import { strings as S } from './strings'
import './ProgramsPage.css'
import { serverDetail, serverText } from '../../lib/i18n'
import { displayDate } from '../../lib/dates'

/* Today as `YYYY-MM-DD` in the reader's own calendar day — the form the row dates arrive in. */
const todayIso = () => {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
}

/* THE SWITCH IS NOT THE CALENDAR. `isActive` is the administrator's switch, and it stays on
   after the last course has ended — so a programme that finished in August read "نشط" in
   October (Taiga #1521). The calendar is reported BESIDE the switch rather than in place of
   it: a programme whose end date has passed keeps its switch chip and gains a second
   "منتهي" one, the way a user row carries both "معطل" and "منتهي الصلاحية". The switch is
   left alone, since it is also what keeps a finished programme visible to learners. */
const isExpired = (row) => Boolean(row.endDate) && String(row.endDate).slice(0, 10) < todayIso()

/* The state of a row as chips, in reading order: the switch first, then the calendar. The
   two are independent, so the calendar is reported on a disabled programme as well — the
   end date has passed whether or not the administrator has since turned the switch off. */
const statusChips = (row) => {
  const chips = row.isActive
    ? [{ id: 'active', label: S.status.active, tone: 'success' }]
    : [{ id: 'inactive', label: S.status.inactive, tone: 'danger' }]
  if (isExpired(row)) chips.push({ id: 'expired', label: S.status.expired, tone: 'dark' })
  return chips
}

/**
 * ادارة البرامج التدريبية — Views/ProgramsManagement/Table.cshtml.
 *
 * The four dials repeat the dashboard aggregate, so both read the same figures.
 * The notify action is disabled on inactive programmes, keeping the legacy's
 * separate tooltip for that state.
 */
export default function ProgramsPage() {
  const navigate = useNavigate()
  const toast = useToast()
  const [query, setQuery] = useRemembered('programs:query', '')
  const [rows, setRows] = useState([])
  /* The saved running order, as ids. The table has its own sort, so the ordering screen
     cannot be seeded from `rows`: it has to open on what was last SAVED. */
  const [ordered, setOrdered] = useState([])
  const [shares, setShares] = useState(null)
  const [loading, setLoading] = useState(true)
  /* A FAILED load is not an empty one: without this the table renders its "no records"
     state for a database fault, which tells the operator the opposite of the truth. */
  const [loadError, setLoadError] = useState(null)
  const [pendingDelete, setPendingDelete] = useState(null)
  const [pendingClone, setPendingClone] = useState(null)
  const [reloadKey, setReloadKey] = useState(0)
  const [orderOpen, setOrderOpen] = useState(false)
  const [savingOrder, setSavingOrder] = useState(false)

  /** One place that turns an action's outcome into a message and a refresh. */
  const run = useCallback(async (call, onDone, { done = S.messages.saved, failed = S.fail.order } = {}) => {
    const outcome = await call
    if (!outcome.ok) {
      toast.error(serverDetail(outcome.message) || failed)
      return outcome
    }
    toast.success(serverText(outcome.message) || done)
    onDone?.()
    setReloadKey((key) => key + 1)
    return outcome
  }, [toast])

  useEffect(() => {
    listPrograms()
      .then((response) => {
        setLoadError(null)
        setRows(response.rows)
        setOrdered(response.ordered ?? [])
      /* `DashboardDto` gives counts, not a pass/fail breakdown, and nothing in this slice
         produces one — so the dials stay hidden rather than showing invented figures.
         `OutcomeShare` renders nothing for a null. Registered in COSMETIC_UI.md. */
        setShares(null)
        setLoading(false)
      })
      .catch((error) => {
        setLoadError(error)
        /* Without this the spinner never stops: a failed call left `loading` true and the
           screen sat there loading nothing, with no reason anywhere. */
        setRows([])
        setLoading(false)
        toast.error(serverDetail(error?.message) || S.fail.list)
      })
  }, [reloadKey, toast])

  /* WHAT THE ORDERING SCREEN LISTS: the ACTIVE programmes, in the order last saved.
     Active only, because that is exactly the set the server orders and writes back
     (`OrderedPrograms` in ViewProgramsManagementPageHandler) — listing a disabled
     programme here would offer a position it will never be shown in. A programme the
     saved order does not name is new since it was set, and joins the end. */
  const orderedPrograms = useMemo(() => {
    const active = rows.filter((row) => row.isActive)
    const byId = new Map(active.map((row) => [row.id, row]))
    const named = ordered.map((id) => byId.get(id)).filter(Boolean)
    const seen = new Set(named.map((row) => row.id))
    return [...named, ...active.filter((row) => !seen.has(row.id))]
  }, [rows, ordered])

  const columns = useMemo(
    () => [
      {
        key: 'name',
        header: S.columns.name,
        render: (row) => (
          <Link className="programs__link" to={`/programs/${row.id}`}>
            <bdi>{row.name}</bdi>
          </Link>
        ),
      },
      { key: 'groupsCount', header: S.columns.groups },
      { key: 'coursesCount', header: S.columns.courses },
      {
        key: 'startDate',
        header: S.columns.startDate,
        render: (row) => (
          <span className="programs__date" dir="ltr">
            {displayDate(row.startDate)}
          </span>
        ),
      },
      {
        key: 'endDate',
        header: S.columns.endDate,
        render: (row) => (
          <span className="programs__date" dir="ltr">
            {displayDate(row.endDate)}
          </span>
        ),
      },
      {
        key: 'isActive',
        header: S.columns.status,
        align: 'center',
        value: (row) => statusChips(row).map((chip) => chip.label).join(' '),
        /* The switch as a mark (2026-10-22 — the legacy cell was a bare check or cross too);
           the calendar's "ended" stays a word beside it, since it is not the switch. */
        render: (row) => (
          <span className="programs__chips">
            <ActiveMark
              active={Boolean(row.isActive)}
              label={row.isActive ? S.status.active : S.status.inactive}
            />
            {isExpired(row) && <StatusBadge tone="dark">{S.status.expired}</StatusBadge>}
          </span>
        ),
      },
      {
        key: 'actions',
        header: <Pencil size={15} strokeWidth={1.75} />,
        align: 'center',
        width: '112px',
        sortable: false,
        searchable: false,
        render: (row) => (
          <RowActions
            actions={[
              {
                id: 'report',
                icon: ChartPie,
                label: S.row.report,
                tone: 'info',
                /* The reports screen already picks a programme; it now takes that
                   choice from the URL, so arriving from a row lands on that
                   programme's report rather than on an empty picker. */
                onClick: () => navigate(`/programs-reports?program=${row.id}`),
              },
              { id: 'edit', icon: Pencil, label: S.row.edit, onClick: () => navigate(`/programs/${row.id}/edit`) },
              {
                id: 'clone',
                icon: Copy,
                label: S.row.clone,
                tone: 'primary',
                /* Asks first, as the legacy did: a copy is a whole new programme in the
                   list, and one stray click used to make it (Taiga #1531). */
                onClick: () => setPendingClone(row),
              },
              /* TWO ACTIONS ARE NOT HERE, and both were worse than missing.
               *
               * `إرسال تنبيه` — the legacy posts a programme id to
               * `ProgramsManagement/AddNotification`; this project's handler takes a
               * `CourseId`, looks the row up in `Courses` and reads its programme from
               * there. There is no way to name a PROGRAMME to it, and sending a
               * programme id in the course field would schedule the notification
               * against whichever course happens to share that number. The course rows
               * on the detail page carry this action, where it is expressible.
               *
               * `نقل النتائج السابقة` — posts to
               * `ProgramsManagement/MigrateCourseResults`, which does not exist in this
               * project. Its confirm dialog answered with a SUCCESS message and a count
               * taken from the row's own group total: a migration that never ran,
               * reported as having moved a number of users it invented.
               *
               * Both in COSMETIC_UI.md with what they would need. */
              {
                id: 'delete',
                icon: Trash2,
                label: S.row.delete,
                danger: true,
                onClick: () => setPendingDelete(row),
              },
            ]}
          />
        ),
      },
    ],
    [navigate],
  )

  return (
    <>
      <PageHeader
        title={S.title}
        count={loading ? undefined : rows.length}
        actions={
          <>
            <Button onClick={() => navigate('/programs/new')}>
              <Plus size={16} strokeWidth={2} />
              {S.actions.create}
            </Button>
            {/* Three buttons of equal weight competed for one job; only `إنشاء` is
                the page's primary action. */}
            <Button variant="soft" onClick={() => navigate('/programs/quick')}>
              <Zap size={16} strokeWidth={1.75} />
              {S.actions.quickCreate}
            </Button>
            {/* Had no handler since the page was built, while `SaveProgramsOrder`
                has been there the whole time. */}
            <Button variant="soft" disabled={loading} onClick={() => setOrderOpen(true)}>
              <ArrowUpDown size={16} strokeWidth={1.75} />
              {S.actions.ordering}
            </Button>
          </>
        }
      />

      {/* The same four dials as the dashboard, at a quarter of the height, so the
          list starts above the fold instead of 170px below it. */}
      <OutcomeShare
        shares={shares}
        labels={S.shares}
        notes={{ incomplete: S.shares.incompleteNote, absent: S.shares.absentNote }}
      />

      <FilterBar search={{ value: query, onChange: setQuery, placeholder: S.columns.name, label: S.searchLabel }} />

      <DataTable
        stateKey="programs"
        error={loadError}
        columns={columns}
        rows={rows}
        loading={loading}
        query={query}
        controls={false}
        getRowId={(row) => row.id}
        rowTo={(row) => `/programs/${row.id}`}
        initialSort={{ key: 'name', dir: 'asc' }}
      />

      <ProgramOrderModal
        open={orderOpen}
        programs={orderedPrograms}
        saving={savingOrder}
        onClose={() => setOrderOpen(false)}
        onSubmit={async (orderedIds) => {
          setSavingOrder(true)
          /* Reloads on success, so reopening the screen shows what was just saved rather
             than the list this page happened to be holding. */
          await run(saveProgramsOrder(orderedIds), () => {
            setOrderOpen(false)
            setReloadKey((key) => key + 1)
          })
          setSavingOrder(false)
        }}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        message={S.deleteConfirm}
        /* TYPE THE NAME BACK, as the legacy screen does. Deleting a programme destroys its
           courses, its exams and every learner's history against them — far too much to sit
           behind one press of a red button. */
        confirmPhrase={pendingDelete?.name}
        confirmPhraseLabel={S.deleteByName}
        confirmLabel={S.row.delete}
        cancelLabel={S.close}
        onConfirm={() => run(deleteProgram(pendingDelete.id), () => setPendingDelete(null))}
        onCancel={() => setPendingDelete(null)}
      />

      <ConfirmDialog
        open={!!pendingClone}
        tone="primary"
        title={S.cloneTitle}
        message={S.cloneConfirm}
        confirmLabel={S.row.clone}
        cancelLabel={S.close}
        /* The endpoint takes the copy's two names, per language — the server does not
           invent them, and a clone with the original's name would not save. */
        onConfirm={() =>
          run(cloneProgramAsCopy(pendingClone), () => setPendingClone(null), {
            done: S.messages.programCopied,
            failed: S.fail.copy,
          })
        }
        onCancel={() => setPendingClone(null)}
      />
    </>
  )
}
